import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["tab", "content", "defaultTab"];
  static classes = ["selected"];
  static values = { history: Boolean, legacyEvents: String };

  connect() {
    this.uid = this.uid || `tabs-${Math.random().toString(36).slice(2, 8)}`;
    this.setupAria();
    this.bindKeydown();

    this.morphGuard =
      this.morphGuard ||
      ((event) => {
        const { target, detail } = event;
        if (!["class", "aria-selected", "tabindex"].includes(detail.attributeName)) return;
        if (this.tabTargets.includes(target) || this.contentTargets.includes(target)) {
          event.preventDefault();
        }
      });
    document.addEventListener("turbo:before-morph-attribute", this.morphGuard);

    // Idiomorph can recreate tab/panel nodes it fails to match (they carry no
    // ids), losing the client-held active state while this controller's element
    // persists without reconnecting. legacy: true also refills ajax panels.
    this.morphReassert =
      this.morphReassert || (() => this.showTab(this.currentIndex ?? 0, { legacy: true }));
    document.addEventListener("turbo:morph", this.morphReassert);

    let defaultIndex = 0;
    if (this.hasDefaultTabTarget) {
      const defaultTab = this.defaultTabTarget;
      defaultIndex = this.tabTargets.indexOf(defaultTab);
      if (defaultIndex === -1) {
        defaultIndex = 0;
      }
    }

    const hashIndex = this.historyValue ? this.indexForHash(window.location.hash) : -1;
    this.showTab(hashIndex >= 0 ? hashIndex : defaultIndex, { legacy: hashIndex >= 0 });
  }

  disconnect() {
    document.removeEventListener("turbo:before-morph-attribute", this.morphGuard);
    document.removeEventListener("turbo:morph", this.morphReassert);
  }

  setupAria() {
    const tablist = this.tabTargets[0]?.parentElement;
    if (tablist) tablist.setAttribute("role", "tablist");

    this.tabTargets.forEach((tab, index) => {
      const content = this.contentTargets[index];
      const name = tab.dataset.tabsName || String(index);

      if (!tab.id) tab.id = `${this.uid}-tab-${name}`;
      tab.setAttribute("role", "tab");

      if (content) {
        if (!content.id) content.id = `${this.uid}-panel-${name}`;
        tab.setAttribute("aria-controls", content.id);
        content.setAttribute("role", "tabpanel");
        content.setAttribute("aria-labelledby", tab.id);
      }
    });
  }

  bindKeydown() {
    this.tabTargets.forEach((tab) => {
      if (tab.dataset.tabsKeyBound) return;
      if (tab.dataset.action && tab.dataset.action.includes("keydown->tabs#key")) return;

      tab.addEventListener("keydown", this.key.bind(this));
      tab.dataset.tabsKeyBound = "true";
    });
  }

  selectTab(event) {
    const index = this.tabTargets.indexOf(event.currentTarget);
    this.activate(index);
  }

  key(event) {
    const tabs = this.tabTargets;
    const currentIndex = tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        this.focusTab((currentIndex - 1 + tabs.length) % tabs.length);
        break;
      case "ArrowRight":
        event.preventDefault();
        this.focusTab((currentIndex + 1) % tabs.length);
        break;
      case "Home":
        event.preventDefault();
        this.focusTab(0);
        break;
      case "End":
        event.preventDefault();
        this.focusTab(tabs.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        this.activate(currentIndex);
        break;
    }
  }

  focusTab(index) {
    this.setRovingTabindex(index);
    this.tabTargets[index]?.focus();
  }

  activate(index) {
    this.showTab(index, { legacy: true });

    if (this.historyValue) {
      const name = this.tabTargets[index]?.dataset.tabsName;
      if (name) history.replaceState(null, "", `#${name}`);
    }
  }

  indexForHash(hash) {
    if (!hash) return -1;
    const name = hash.replace(/^#\/?/, "");
    return this.tabTargets.findIndex((tab) => tab.dataset.tabsName === name);
  }

  setRovingTabindex(index) {
    this.tabTargets.forEach((tab, i) => {
      tab.setAttribute("tabindex", i === index ? "0" : "-1");
    });
  }

  showTab(index, { legacy = false } = {}) {
    this.currentIndex = index;
    this.setRovingTabindex(index);

    this.tabTargets.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle(this.selectedClass, active); // Add 'selected' class to the active tab
      tab.setAttribute("aria-selected", active ? "true" : "false");
    });

    this.contentTargets.forEach((content, i) => {
      if (content.classList.contains(this.selectedClass)) {
        content.dispatchEvent(
          new CustomEvent("tabs:deselected", {
            bubbles: true,
            detail: { index },
          })
        ); // Dispatch custom event
      }
      content.classList.toggle("no-show", i !== index);
      content.classList.toggle(this.selectedClass, i === index); // Add 'selected' class to the active content
      if (i === index) {
        content.dispatchEvent(
          new CustomEvent("tabs:selected", { bubbles: true, detail: { index } })
        ); // Dispatch custom event
      }
    });

    if (legacy) this.dispatchLegacyEvent(index);
  }

  dispatchLegacyEvent(index) {
    if (!this.legacyEventsValue) return;

    const tab = this.tabTargets[index];
    const content = this.contentTargets[index];
    const name = tab?.dataset.tabsName;
    const eventName = this.legacyEventsValue === "named" && name ? `tab-open-${name}` : "tab-open";

    [tab, content].forEach((el) => {
      el?.dispatchEvent(new CustomEvent(eventName, { bubbles: true, cancelable: true }));
    });
  }
}
